import { z } from 'zod';
import { decimalString, positiveDecimal, rate } from './primitives.js';

export const quantityRefSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('OUTPUT'),
    familyCode: z.string().min(1).max(60),
    outputKey: z.string().min(1).max(60),
    levelIds: z.array(z.uuid()).optional(),
    typeIds: z.array(z.uuid()).optional(),
    /** Ligne issue d'un calculateur de la bibliothèque : la génération du quantitatif ne la touche pas. */
    library: z.boolean().optional(),
  }),
  z.object({ source: z.literal('BACKFILL') }),
]);
export type QuantityRefInput = z.infer<typeof quantityRefSchema>;

export const LINE_STATUSES = ['NORMAL', 'PM', 'OPTION', 'EXCLUDED'] as const;

export const lineCreateSchema = z
  .object({
    chapterId: z.uuid(),
    designation: z.string().trim().min(1).max(500),
    unit: z.string().trim().min(1).max(20),
    quantityMode: z.enum(['AUTO', 'MANUAL']).default('MANUAL'),
    quantityRef: quantityRefSchema.optional(),
    quantityManual: positiveDecimal.nullable().optional(),
    lossRate: rate.default('0'),
    status: z.enum(LINE_STATUSES).default('NORMAL'),
    notes: z.string().max(5000).nullable().optional(),
    justification: z.string().max(5000).nullable().optional(),
    labor: z.lazy(() => lineLaborSchema).nullable().optional(),
  })
  .refine((l) => l.quantityMode !== 'AUTO' || !!l.quantityRef, { error: 'Une ligne automatique doit référencer des éléments (quantityRef)' });

const taskCode = z.string().regex(/^[A-Z0-9_-]{2,30}$/);
export const lineLaborSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('AUTO') }),
  z.object({ method: z.literal('UNIT_PRICE'), task: taskCode }),
  z.object({ method: z.literal('PRODUCTIVITY'), task: taskCode, crew: taskCode.optional(), outputPerDay: positiveDecimal.optional() }),
  z.object({ method: z.literal('NONE') }),
]);

export const lineUpdateSchema = z.object({
  version: z.number().int().positive(),
  labor: lineLaborSchema.nullable().optional(),
  /** PU de vente imposé ; null = PU calculé (déboursé × K). */
  unitPriceManual: positiveDecimal.nullable().optional(),
  chapterId: z.uuid().optional(),
  designation: z.string().trim().min(1).max(500).optional(),
  unit: z.string().trim().min(1).max(20).optional(),
  quantityMode: z.enum(['AUTO', 'MANUAL']).optional(),
  quantityManual: decimalString.nullable().optional(),
  lossRate: rate.optional(),
  status: z.enum(LINE_STATUSES).optional(),
  notes: z.string().max(5000).nullable().optional(),
  justification: z.string().max(5000).nullable().optional(),
  /** Texte du bordereau des prix, s'il diffère de la désignation. */
  bpuText: z.string().trim().max(2000).nullable().optional(),
});

export const reorderSchema = z.object({ chapterId: z.uuid(), lineIds: z.array(z.uuid()).min(1) });

export const generateQuantitiesSchema = z.object({
  /** Une ligne par famille et par niveau (défaut), par famille pour tout le projet, ou par type. */
  groupBy: z.enum(['LEVEL', 'PROJECT', 'TYPE']).default('LEVEL'),
});

export const chapterCreateSchema = z.object({
  code: z.string().trim().regex(/^[A-Z0-9_-]{2,20}$/, { error: 'Code en majuscules (A-Z, 0-9, _ -)' }),
  name: z.string().trim().min(2).max(120),
  sortOrder: z.number().int().optional(),
});

export const linkCreateSchema = z.object({
  sourceInstanceId: z.uuid(),
  targetInstanceId: z.uuid(),
  sourceOutputKey: z.string().min(1).max(60).default('volume'),
  targetOutputKey: z.string().min(1).max(60).default('volume'),
  factor: positiveDecimal.default('1'),
  note: z.string().max(300).nullable().optional(),
});

export const quotationCreateSchema = z.object({ title: z.string().trim().min(2).max(200), label: z.string().trim().max(120).optional() });
export const versionCreateSchema = z.object({ label: z.string().trim().max(120).optional() });
